import React, { useLayoutEffect, useRef } from 'react';
import { bool, func, node, object, shape, string } from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Form as FinalForm, Field } from 'react-final-form';
import classNames from 'classnames';

import { Form, NamedLink } from '../../../../components';
import { useConfiguration } from '../../../../context/configurationContext';
import { useRouteConfiguration } from '../../../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { createResourceLocatorString } from '../../../../util/routes';
import { isMainSearchTypeKeywords } from '../../../../util/search';
import { stringify } from '../../../../util/urlHelpers';
import SectionContainer from '../../../PageBuilder/SectionBuilder/SectionContainer';
import sectionBuilderCss from '../../../PageBuilder/SectionBuilder/SectionBuilder.module.css';
import { ArrowLinkIcon } from '../MobileHomeIcons';
import heroMobileTablet from '../../../../assets/hero-mobile-tablet.png';
import heroMobileHomepage from '../../../../assets/homepage-hero-mobile.webp';

import css from './SectionMarketplaceHero.module.css';

const LA_SEARCH = { address: 'Los Angeles' };

const CATEGORY_CHIPS = [
  { id: 'browse', labelId: 'LandingPage.mobileHome.hero.chip.browseDecor', search: {}, lead: true },
  {
    id: 'props',
    labelId: 'LandingPage.mobileHome.hero.chip.props',
    search: { pub_category: 'props' },
  },
  {
    id: 'events',
    labelId: 'LandingPage.mobileHome.hero.chip.events',
    search: { pub_category: 'events' },
  },
  {
    id: 'propsAndEvents',
    labelId: 'LandingPage.mobileHome.hero.chip.propsAndEvents',
    search: { pub_category: 'propsAndEvents' },
  },
  {
    id: 'small',
    labelId: 'LandingPage.mobileHome.hero.chip.smallProps',
    search: { pub_size: 'smallProps' },
  },
  {
    id: 'mid',
    labelId: 'LandingPage.mobileHome.hero.chip.midSizeProps',
    search: { pub_size: 'midSizeProps' },
  },
  {
    id: 'large',
    labelId: 'LandingPage.mobileHome.hero.chip.largeProps',
    search: { pub_size: 'largeProps' },
  },
];

const HeroSearchMagnifierIcon = () => (
  <svg
    className={css.heroSearchIcon}
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.3"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <circle cx="10.5" cy="10.5" r="7" fill="none" />
    <line x1="16" y1="16" x2="21" y2="21" />
  </svg>
);

const PinOutlineIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 21 c-4.5-4.6-6.8-8-6.8-11a6.8 6.8 0 0 1 13.6 0 c0 3-2.3 6.4-6.8 11Z" fill="none" />
    <circle cx="12" cy="9.8" r="2.4" fill="none" />
  </svg>
);

const ChatOutlineIcon = () => (
  <svg
    className={css.trustIcon}
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.1"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path
      d="M21 11.6 a8.4 7.6 0 0 1-8.5 7.6 c-1.1 0-2.2-.2-3.2-.5 L4 20 l1.5-3.6 A7.4 7.4 0 0 1 3.9 11.6 a8.4 7.6 0 0 1 17.1 0Z"
      fill="none"
    />
  </svg>
);

const TagOutlineIcon = () => (
  <svg
    className={css.trustIcon}
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.1"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path
      d="M20.6 13.3 L13.4 20.5 a2 2 0 0 1-2.8 0 L3.5 13.4 V3.5 h9.9 l7.2 7 a2 2 0 0 1 0 2.8Z"
      fill="none"
    />
    <circle className={css.iconDot} cx="8.3" cy="8.3" r="1.4" />
  </svg>
);

const HeroSearch = () => {
  const config = useConfiguration();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const searchInputRef = useRef(null);

  const isKeywordsSearch = isMainSearchTypeKeywords(config);
  const placeholder = intl.formatMessage({
    id: 'SectionMarketplaceHero.searchPlaceholder',
  });

  const buildHeroSearchParams = values => {
    const trimmed = values?.keywords?.trim();
    if (isKeywordsSearch) {
      return {
        ...LA_SEARCH,
        ...(trimmed ? { keywords: trimmed } : {}),
      };
    }
    return {
      ...LA_SEARCH,
      ...(trimmed ? { keywords: trimmed } : {}),
    };
  };

  const onSearchSubmit = values => {
    history.push(
      createResourceLocatorString(
        'SearchPage',
        routeConfiguration,
        {},
        buildHeroSearchParams(values)
      )
    );
    searchInputRef.current?.blur();
  };

  return (
    <div className={css.heroSearch}>
      <FinalForm
        onSubmit={onSearchSubmit}
        render={({ handleSubmit }) => (
          <Form
            className={css.heroSearchForm}
            onSubmit={handleSubmit}
            enforcePagePreloadFor="SearchPage"
          >
            <div className={css.heroSearchField}>
              <button className={css.heroSearchSubmit} type="submit" aria-label={placeholder}>
                <HeroSearchMagnifierIcon />
              </button>
              <Field
                name="keywords"
                render={({ input }) => (
                  <input
                    {...input}
                    className={css.heroSearchInput}
                    id="hero-keyword-search"
                    type="text"
                    inputMode="search"
                    enterKeyHint="search"
                    placeholder={placeholder}
                    autoComplete="off"
                    ref={searchInputRef}
                  />
                )}
              />
              <span className={css.locChip}>
                <PinOutlineIcon className={css.locChipIcon} />
                <FormattedMessage id="LandingPage.mobileHome.hero.locChip" />
              </span>
            </div>
          </Form>
        )}
      />
    </div>
  );
};

const SectionMarketplaceHero = props => {
  const { sectionId, className, rootClassName, appearance, options } = props;
  const intl = useIntl();
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const panelRef = useRef(null);
  const btnMeasureRef = useRef(null);

  useLayoutEffect(() => {
    const panel = panelRef.current;
    const measure = btnMeasureRef.current;
    if (!panel || !measure) {
      return undefined;
    }
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches) {
      return undefined;
    }

    let rafId = 0;
    const scheduleSync = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        const p = panelRef.current;
        const m = btnMeasureRef.current;
        if (!p || !m) {
          return;
        }
        const w = Math.ceil(m.getBoundingClientRect().width);
        if (!w) {
          return;
        }
        const next = `${w}px`;
        if (p.style.getPropertyValue('--cta-btn-w') === next) {
          return;
        }
        p.style.setProperty('--cta-btn-w', next);
      });
    };

    scheduleSync();
    if (typeof ResizeObserver === 'undefined') {
      return () => {
        if (rafId) {
          cancelAnimationFrame(rafId);
        }
      };
    }
    const ro = new ResizeObserver(scheduleSync);
    ro.observe(measure);
    return () => {
      ro.disconnect();
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={classNames(rootClassName || css.root)}
      sectionContentClassName={css.heroSectionContent}
      appearance={appearance}
      options={fieldOptions}
    >
      <div className={css.layout}>
        <HeroSearch />
        <div className={css.heroMain}>
          <div className={css.heroBand}>
            <div className={css.visual}>
              <img
                className={css.heroArt}
                src={heroMobileTablet}
                alt=""
                decoding="async"
                fetchpriority="high"
              />
              <img
                className={css.heroArtMobile}
                src={heroMobileHomepage}
                alt={intl.formatMessage({ id: 'LandingPage.mobileHome.hero.heroImageAlt' })}
                decoding="async"
                fetchpriority="high"
              />
            </div>
            <div className={css.mobileHeroCard}>
              <svg
                className={css.mobileHeroRing}
                width="150"
                height="150"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <g fill="#4E3B27">
                  <circle cx="20.5" cy="12" r="1.1" />
                  <circle cx="18" cy="18" r="1.1" />
                  <circle cx="12" cy="20.5" r="1.1" />
                  <circle cx="6" cy="18" r="1.1" />
                  <circle cx="3.5" cy="12" r="1.1" />
                  <circle cx="6" cy="6" r="1.1" />
                  <circle cx="12" cy="3.5" r="1.1" />
                </g>
                <circle cx="18" cy="6" r="1.3" fill="#B0602F" />
              </svg>
              <p className={css.mobileHeroHeadline}>
                <FormattedMessage id="LandingPage.mobileHome.hero.mobileHeadline" />
                <span>
                  <FormattedMessage id="LandingPage.mobileHome.hero.mobileSubhead" />
                </span>
              </p>
              <NamedLink
                name="SearchPage"
                to={{ search: `?${stringify(LA_SEARCH)}` }}
                className={css.mobileCta}
              >
                <FormattedMessage id="LandingPage.mobileHome.hero.browseCta" />
              </NamedLink>
            </div>
            <div className={css.heroSpacer} />
          </div>
          <div ref={panelRef} className={css.ctaPanel}>
            <p className={css.tagline}>
              <FormattedMessage id="LandingPage.mobileHome.hero.tagline" />
            </p>
            <span ref={btnMeasureRef} className={css.ctaBtnMeasure}>
              <NamedLink
                name="SearchPage"
                to={{ search: `?${stringify(LA_SEARCH)}` }}
                className={classNames(sectionBuilderCss.ctaButton, css.ctaButtonInPanel)}
              >
                <FormattedMessage id="LandingPage.mobileHome.hero.browseCta" />
              </NamedLink>
            </span>
          </div>
        </div>
        <p className={css.taglineMobile}>
          <FormattedMessage id="LandingPage.mobileHome.hero.tagline" />
        </p>
        <div className={css.searchExtras}>
          <div className={css.chips}>
            {CATEGORY_CHIPS.map(chip => (
              <NamedLink
                key={chip.id}
                className={chip.lead ? css.chipLead : css.chip}
                name="SearchPage"
                to={{
                  search:
                    chip.search && Object.keys(chip.search).length
                      ? `?${stringify(chip.search)}`
                      : '',
                }}
              >
                {chip.labelId ? <FormattedMessage id={chip.labelId} /> : null}
                {chip.lead ? <ArrowLinkIcon size={12} strokeWidth={2.6} /> : null}
              </NamedLink>
            ))}
          </div>
          <div className={css.trust}>
            <span className={css.trustItem}>
              <PinOutlineIcon className={classNames(css.trustIcon, css.trustPinIcon)} />
              <FormattedMessage id="LandingPage.mobileHome.hero.trust.localPickup" />
            </span>
            <span className={css.trustSep} />
            <span className={css.trustItem}>
              <ChatOutlineIcon />
              <FormattedMessage id="LandingPage.mobileHome.hero.trust.messageOwners" />
            </span>
            <span className={css.trustSep} />
            <span className={css.trustItem}>
              <TagOutlineIcon />
              <FormattedMessage id="LandingPage.mobileHome.hero.trust.freeToList" />
            </span>
          </div>
        </div>
      </div>
    </SectionContainer>
  );
};

const propTypeOption = shape({
  fieldComponents: shape({ component: node, pickValidProps: func }),
});

SectionMarketplaceHero.defaultProps = {
  className: null,
  rootClassName: null,
  appearance: null,
  isInsideContainer: false,
  options: null,
};

SectionMarketplaceHero.propTypes = {
  sectionId: string,
  className: string,
  rootClassName: string,
  appearance: object,
  isInsideContainer: bool,
  options: propTypeOption,
};

export default SectionMarketplaceHero;
